<template>
  <div class="scenario-manager space-y-6 animate-[fadeIn_0.6s_ease-out]">
    <!-- 页面标题 -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F5D76E] to-[#F2C94C] flex items-center justify-center shadow-sm">
          <svg class="w-5 h-5 text-[#1B3C59]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <h3 class="text-xl font-black text-slate-900">场景题库</h3>
        </div>
      </div>
      <button
        v-if="hasPerm(PERMISSIONS.CREATE_SCENARIO)"
        @click="handleAdd"
        class="flex items-center gap-1.5 px-4 py-2 bg-[#F2C94C] text-white text-sm font-medium rounded-full hover:bg-[#E5B73B] transition-colors shadow-sm"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        新增场景
      </button>
    </div>

    <!-- 前台关联提示 -->
    <div class="bg-blue-50/80 backdrop-blur-sm border border-blue-100/60 rounded-2xl p-4 flex items-start gap-3 text-sm text-blue-700">
      <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div>
        <p class="font-medium mb-1">前台关联</p>
        <p class="text-blue-600">场景题目出现在前台「练一练」情景模拟页面，是用户进行管理能力演练的核心题库</p>
      </div>
    </div>

    <!-- 搜索 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-5 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <div class="flex items-center gap-3">
        <div class="relative flex-1 max-w-md">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="搜索场景描述..."
            class="w-full px-4 py-1.5 pl-9 bg-slate-50 border border-slate-200 rounded-full text-sm focus:outline-none focus:border-[#F2C94C] focus:ring-2 focus:ring-[#F2C94C]/20 transition-all"
          />
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>
    </div>

    <!-- 场景列表 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <el-table :data="filteredScenarios" stripe size="small" style="width: 100%">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column label="描述" min-width="300">
          <template #default="{ row }">
            <span class="text-sm text-slate-700 line-clamp-2">{{ row.description }}</span>
          </template>
        </el-table-column>
        <el-table-column label="选项" width="100" align="center">
          <template #default="{ row }">
            {{ row.options?.length || 0 }}
          </template>
        </el-table-column>
        <el-table-column prop="practiceCount" label="练习次数" width="100" align="center" />
        <el-table-column prop="accuracyRate" label="正确率" width="100" align="center">
          <template #default="{ row }">
            <span class="text-sm font-medium">{{ row.accuracyRate }}%</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120" fixed="right">
          <template #default="{ row }">
            <div class="flex items-center gap-2">
              <button v-if="hasPerm(PERMISSIONS.EDIT_SCENARIO)" @click="handleEdit(row)" class="text-[#F2C94C] hover:text-[#E5B73B] text-sm font-medium transition-colors">编辑</button>
              <button v-if="hasPerm(PERMISSIONS.DELETE_SCENARIO)" @click="handleDelete(row)" class="text-red-400 hover:text-red-500 text-sm font-medium transition-colors">删除</button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="filteredScenarios.length === 0" class="text-center py-12">
        <svg class="w-12 h-12 text-slate-200 mx-auto mb-3" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div class="text-slate-400 text-sm">暂无场景</div>
      </div>
    </div>

    <!-- 编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="720px"
      :close-on-click-modal="false"
      @closed="resetForm"
    >
      <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-5 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
        <el-form :model="form" label-width="90px" size="small">
          <el-form-item label="场景描述" required>
            <el-input
              v-model="form.description"
              type="textarea"
              :rows="4"
              placeholder="请输入场景描述..."
            />
          </el-form-item>

          <div class="mb-4">
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-medium text-slate-700">选项列表</span>
              <button
                @click="addOption"
                type="button"
                class="text-xs text-[#F2C94C] hover:text-[#E5B73B] font-medium"
              >
                + 添加选项
              </button>
            </div>

            <div v-for="(opt, idx) in form.options" :key="idx" class="bg-slate-50 rounded-lg p-3 mb-2">
              <div class="flex items-start gap-2">
                <div class="flex-1 space-y-2">
                  <el-input v-model="opt.text" size="mini" placeholder="选项文字" />
                  <el-input v-model="opt.feedback" size="mini" type="textarea" :rows="2" placeholder="反馈内容" />
                </div>
                <div class="flex flex-col items-center gap-1">
                  <el-switch v-model="opt.isError" :active-value="false" :inactive-value="true" active-text="正确" inactive-text="错误" size="mini" />
                  <button
                    v-if="form.options.length > 1"
                    @click="removeOption(idx)"
                    type="button"
                    class="text-xs text-red-400 hover:text-red-500"
                  >
                    删除
                  </button>
                </div>
              </div>
            </div>
          </div>

          <el-form-item label="练习次数">
            <el-input v-model.number="form.practiceCount" type="number" disabled />
          </el-form-item>
          <el-form-item label="正确率">
            <el-input v-model.number="form.accuracyRate" type="number" disabled />
          </el-form-item>
        </el-form>
      </div>

      <span slot="footer">
        <button
          @click="dialogVisible = false"
          class="px-4 py-2 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50 transition-colors mr-2"
        >
          取消
        </button>
        <button
          @click="handleSave"
          class="px-5 py-2 bg-[#F2C94C] text-white text-sm font-medium rounded-full hover:bg-[#E5B73B] transition-colors shadow-sm"
        >
          保存
        </button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { logAction } from '../../services/audit'
import { SCENARIO_DATA } from '../../data'
import { PERMISSIONS } from '../../data/adminConfig'

const DEFAULT_OPTION = {
  id: '',
  text: '',
  isError: false,
  feedback: '',
}

const DEFAULT_FORM = {
  key: '',
  id: '',
  description: '',
  options: [{ ...DEFAULT_OPTION, id: 'o1' }],
  practiceCount: 0,
  accuracyRate: 0,
}

export default {
  name: 'ScenarioManager',

  data() {
    return {
      dialogVisible: false,
      dialogTitle: '编辑场景',
      isEdit: false,
      form: { ...DEFAULT_FORM },
      searchQuery: '',
    }
  },

  computed: {
    ...mapState(['scenarios']),
    ...mapGetters(['hasPermission', 'dataScope', 'currentAdminId']),

    PERMISSIONS() {
      return PERMISSIONS
    },

    scenarioList() {
      const dict = this.scenarios || SCENARIO_DATA
      return Object.entries(dict).map(([key, value]) => ({
        key,
        ...value,
      }))
    },

    filteredScenarios() {
      let list = [...this.scenarioList]

      // 数据范围过滤
      if (this.dataScope === 'own') {
        list = list.filter(s => s.createdBy === this.currentAdminId)
      }

      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase()
        list = list.filter(s => s.description.toLowerCase().includes(q))
      }
      return list
    },
  },

  methods: {
    hasPerm(permission) {
      return this.hasPermission(permission)
    },

    handleAdd() {
      this.isEdit = false
      this.dialogTitle = '新增场景'
      this.form = {
        ...DEFAULT_FORM,
        key: `s${Date.now()}`,
        id: `s${Date.now()}`,
        createdBy: this.currentAdminId,
      }
      this.dialogVisible = true
    },

    handleEdit(row) {
      this.isEdit = true
      this.dialogTitle = `编辑场景 - ${row.key}`
      this.form = {
        ...DEFAULT_FORM,
        ...JSON.parse(JSON.stringify(row)),
        options: row.options ? JSON.parse(JSON.stringify(row.options)) : [{ ...DEFAULT_OPTION, id: 'o1' }],
      }
      this.dialogVisible = true
    },

    handleDelete(row) {
      this.$confirm(`确定删除场景「${row.key}」？此操作不可恢复。`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        const dict = { ...(this.scenarios || SCENARIO_DATA) }
        delete dict[row.key]
        this.$store.commit('SET_SCENARIOS', dict)

        logAction({
          action: 'DELETE',
          targetType: 'scenario',
          targetId: row.key,
          reason: `删除场景: ${row.key}`,
          source: 'admin-ui',
          actor: { role: this.$store.state.userRole },
        })

        this.$message.success('删除成功')
      }).catch(() => {})
    },

    handleSave() {
      if (!this.form.description.trim()) {
        this.$message.error('场景描述不能为空')
        return
      }

      const dict = { ...(this.scenarios || SCENARIO_DATA) }
      const savedForm = {
        id: this.form.id,
        description: this.form.description,
        options: this.form.options,
        practiceCount: this.form.practiceCount,
        accuracyRate: this.form.accuracyRate,
      }
      if (!this.isEdit) {
        savedForm.createdBy = this.currentAdminId
      } else {
        const existing = dict[this.form.key]
        if (existing) {
          savedForm.createdBy = existing.createdBy
        }
      }
      dict[this.form.key] = savedForm

      this.$store.commit('SET_SCENARIOS', dict)

      logAction({
        action: this.isEdit ? 'UPDATE' : 'CREATE',
        targetType: 'scenario',
        targetId: this.form.key,
        reason: this.isEdit ? '编辑场景' : '新建场景',
        source: 'admin-ui',
        actor: { role: this.$store.state.userRole },
      })

      this.$message.success('保存成功')
      this.dialogVisible = false
    },

    addOption() {
      const newId = `o${this.form.options.length + 1}`
      this.form.options.push({ ...DEFAULT_OPTION, id: newId })
    },

    removeOption(idx) {
      this.form.options.splice(idx, 1)
    },

    resetForm() {
      this.form = { ...DEFAULT_FORM }
    },
  },
}
</script>
